import { useState, useContext, useEffect, forwardRef, useImperativeHandle } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";

import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";
import SuiBox from "components/SuiBox";
import SuiBadge from "components/SuiBadge";

import Checkbox from "@mui/material/Checkbox";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import Settings from "examples/Icons/Settings";
import Cube from "examples/Icons/Cube";
import SpaceShip from "examples/Icons/SpaceShip";
import api from "services/api";

// Authentication layout components
import SignInLayout from "layouts/upstyled/SigninLayout";

import { AccountContext } from "context/account";
import { StoreContext } from "context/store";
import { useForm } from "react-hook-form";
import PhoneInput from "react-phone-number-input";
import { isPossiblePhoneNumber, formatPhoneNumberIntl } from "react-phone-number-input";

//import LetterLogo from "components/UpStyled/LetterLogo";
import UpStyledLogo from "components/UpStyled/UpStyledLogo";

import Prompt from "components/UpStyled/Prompt";
import PasswordChecklist from "react-password-checklist";
import toast from "react-hot-toast";
import qs from "query-string";
import { decodeToken } from "react-jwt";

import { accountTypeOptions, externalLinks } from "utils/data";

const AccountInfo = forwardRef(
  ({ setIsValidPassword, accountType, register, reset, setValue, ...rest }, ref) => {
    const [agreement, setAgreement] = useState(false);
    const [confirmPassword, setConfirmPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmedPassword, setShowConfirmedPassword] = useState(false);
    const [phone, setPhone] = useState();

    const handleSetAgremment = () => setAgreement(!agreement);

    const account = useContext(AccountContext);
    let location = useLocation();
    const navigate = useNavigate();

    useImperativeHandle(
      ref,
      () => ({
        agreement,
        phone,
        setAgreement,
        setPhone,
      }),
      [agreement, phone]
    );

    const url = window?.location?.href ? window.location.href : location.pathname + location.search;
    const urlObj = qs.parseUrl(url) || {};
    let token = urlObj?.query.token;
    let tokenObj = null;
    try {
      tokenObj = decodeToken(token);
    } catch (e) {
      token = null;
    }

    let isReferMode = false; //if url contains user_email, then we will consider it is referenced by a registed user
    if (tokenObj && tokenObj.user_email) {
      isReferMode = true;
    }

    const handleMouseDownPassword = (event) => {
      event.preventDefault();
    };

    const handleChangePhoneNumber = (value) => {
      setValue("business_phone", value);
      setPhone(value);
    };

    return (
      <SuiBox {...rest}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={12}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  required
                  textTransform="capitalize"
                >
                  Business Name
                </SuiTypography>
              </SuiBox>
              <SuiInput
                placeholder="Business Name"
                required
                {...register("business_name", { required: true })}
              />
            </SuiBox>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                  required
                >
                  Email
                </SuiTypography>
              </SuiBox>

              <SuiInput
                type="email"
                required
                readOnly={isReferMode}
                placeholder="Email"
                {...register("email")}
              />
            </SuiBox>
          </Grid>

          <Grid item xs={12} sm={6}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  required={accountType == "ma"}
                  textTransform="capitalize"
                >
                  Phone Number
                </SuiTypography>
              </SuiBox>
              <PhoneInput
                label="Phone"
                className="upstyled-phone-input h-10 px-2 rounded-lg"
                placeholder="Enter phone number"
                name="business_phone"
                value={phone}
                defaultCountry="US"
                {...register("business_phone", { required: accountType == "ma" })}
                required={accountType == "ma"}
                onChange={handleChangePhoneNumber}
              />
            </SuiBox>
          </Grid>

          <Grid item xs={12} sm={6}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                >
                  First Name
                </SuiTypography>
              </SuiBox>

              <SuiInput placeholder="First Name" {...register("first_name_input")} />
            </SuiBox>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                >
                  Last Name
                </SuiTypography>
              </SuiBox>
              <SuiInput placeholder="Last Name" {...register("last_name_input")} />
            </SuiBox>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                  required
                >
                  Password
                </SuiTypography>
              </SuiBox>

              <SuiInput
                placeholder="Password"
                autoComplete="new-password"
                type={showPassword ? "text" : "password"}
                required
                {...register("new_password")}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setValue("new_password", e.target.value);
                }}
                icon={{
                  direction: "right",
                  component: (
                    <div
                      size="small"
                      style={{ cursor: "pointer" }}
                      onClick={() => setShowPassword(!showPassword)}
                      onMouseDown={handleMouseDownPassword}
                    >
                      {showPassword ? <Visibility color="dark" /> : <VisibilityOff color="dark" />}
                    </div>
                  ),
                }}
              />
            </SuiBox>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                  required
                >
                  Confirm your password
                </SuiTypography>
              </SuiBox>
              <SuiInput
                placeholder="Confirm Password"
                type={showConfirmedPassword ? "text" : "password"}
                required
                {...register("confirm_password")}
                onChange={(e) => setConfirmPassword(e.target.value)}
                icon={{
                  direction: "right",
                  component: (
                    <div
                      size="small"
                      style={{ cursor: "pointer" }}
                      onClick={() => setShowConfirmedPassword(!showConfirmedPassword)}
                      onMouseDown={handleMouseDownPassword}
                    >
                      {showConfirmedPassword ? (
                        <Visibility color="dark" />
                      ) : (
                        <VisibilityOff color="dark" />
                      )}
                    </div>
                  ),
                }}
              />
            </SuiBox>
          </Grid>
          {newPassword ? (
            <Grid item xs={12} sm={12}>
              <div>
                <PasswordChecklist
                  rules={["minLength", "number", "match"]}
                  minLength={8}
                  value={newPassword}
                  valueAgain={confirmPassword}
                  iconSize={9}
                  style={{ fontSize: 12 }}
                  onChange={(isValid) => {
                    setIsValidPassword(isValid);
                  }}
                />
              </div>
            </Grid>
          ) : null}
        </Grid>

        <SuiBox display="flex" alignItems="center" mt={1}>
          <Checkbox checked={agreement} onChange={handleSetAgremment} required name="agreement" />
          <SuiBox ml={0.5}>
            <SuiTypography
              variant="button"
              fontWeight="regular"
              onClick={handleSetAgremment}
              sx={{ cursor: "pointer", userSelect: "none" }}
            >
              &nbsp;&nbsp;I agree the&nbsp;
            </SuiTypography>
            <SuiTypography variant="button" color="dark" component="span">
              <a href={externalLinks["terms-of-service"]} target="_blank">
                Terms and Conditions of Use
              </a>
            </SuiTypography>
            &nbsp;and&nbsp;
            <SuiTypography variant="button" color="dark" component="span">
              <a href={externalLinks["privacy-policy"]} target="_blank">
                Privacy Policy
              </a>
            </SuiTypography>
          </SuiBox>
        </SuiBox>
      </SuiBox>
    );
  }
);

export default AccountInfo;
